import usePendingState from '../../Hooks/usePendingState';
import { InputMask } from '@react-input/mask';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNotification } from '../../Contexts/NotificationContext';
import Loading from '../Loading';

const FormEditContact = () => {
    const { setNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [contactData, setContactData] = useState({
        nome: '',
        cargo: '',
        email: '',
        telefone: '',
        assunto: '',
    });

    const [contactErrorData, setContactErrorData] = useState({
        nome: [],
        cargo: [],
        email: [],
        telefone: [],
        assunto: [],
    });

    const [formLoading, setFormLoading, formPendingRef] = usePendingState();

    const navigate = useNavigate();
    const { id } = useParams();
    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setContactData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const contactResponse = await fetch(`${baseUrl}/manager/contatos/${id}`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (contactResponse.ok) {
                    const contactData = await contactResponse.json();
                    setContactData(contactData.contato || []);
                } else {
                    setNotification({
                        type: 'error',
                        message: 'Erro ao buscar o contato.',
                        show: true,
                    });
                }
            } catch (error) {
                setNotification({
                    type: 'error',
                    message: 'Erro na requisição',
                    show: true,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formPendingRef.current) return;
        setFormLoading(true);

        try {
            const response = await fetch(`${baseUrl}/manager/contatos/atualizar/${id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(contactData),
            });
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: 'Contato atualizado com sucesso!',
                    show: true,
                });
                navigate('/manager/contatos');
            } else {
                if (response.status === 422) {
                    const errors = await response.json();

                    setContactErrorData(errors);
                } else {
                    throw new Error('Erro ao atualizar contato.');
                }
            }
        } catch (error) {
            setNotification({
                type: 'error',
                message: error.message,
                show: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    if (loading)
        return (
            <div className="mx-auto py-20 flex justify-center">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent" />
            </div>
        );

    return (
        <>
            <Link
                to="/manager/contatos"
                className="bg-slate-100 px-4 py-1.5 rounded border border-slate-300 float-right ml-2 transition-all hover:bg-slate-200"
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Voltar
            </Link>

            <h4 className="mb-6 text-xl font-bold text-black">Editar contato</h4>

            <div className="flex flex-col">
                <form onSubmit={handleSubmit} className="max-w-5xl pb-10">
                    <div className="mb-6 2xl:mb-10">
                        <div className="mb-3 flex gap-5 flex-col lg:flex-row w-full lg:w-3/4">
                            <div className="w-1/2">
                                <label className="block text-sm font-medium mb-1">Nome</label>
                                <input
                                    type="text"
                                    name="nome"
                                    value={contactData.nome}
                                    onClick={() => setContactErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {contactErrorData.nome && contactErrorData.nome[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {contactErrorData.nome[0]}{' '}
                                        <span
                                            onClick={() => setContactErrorData((prevData) => ({ ...prevData, nome: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/2">
                                <label className="block text-sm font-medium mb-1">Cargo</label>
                                <input
                                    type="text"
                                    name="cargo"
                                    value={contactData.cargo}
                                    onClick={() => setContactErrorData((prevData) => ({ ...prevData, cargo: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {contactErrorData.cargo && contactErrorData.cargo[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {contactErrorData.cargo[0]}{' '}
                                        <span
                                            onClick={() => setContactErrorData((prevData) => ({ ...prevData, cargo: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row w-full lg:w-3/4">
                            <div className="w-1/2">
                                <label className="block text-sm font-medium mb-1">E-mail</label>
                                <input
                                    type="text"
                                    name="email"
                                    value={contactData.email}
                                    onClick={() => setContactErrorData((prevData) => ({ ...prevData, email: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {contactErrorData.email && contactErrorData.email[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {contactErrorData.email[0]}{' '}
                                        <span
                                            onClick={() => setContactErrorData((prevData) => ({ ...prevData, email: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>

                            <div className="w-1/2">
                                <label className="block text-sm font-medium mb-1">Telefone</label>
                                <InputMask
                                    type="text"
                                    name="telefone"
                                    mask="(__) _____-____"
                                    replacement={{ _: /\d/ }}
                                    value={contactData.telefone}
                                    onClick={() => setContactErrorData((prevData) => ({ ...prevData, telefone: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                />
                                {contactErrorData.telefone && contactErrorData.telefone[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {contactErrorData.telefone[0]}{' '}
                                        <span
                                            onClick={() => setContactErrorData((prevData) => ({ ...prevData, telefone: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="mb-3 flex gap-5 flex-col lg:flex-row w-full lg:w-3/4">
                            <div className="w-full">
                                <label className="block text-sm font-medium mb-1">Assunto</label>
                                <textarea
                                    name="assunto"
                                    value={contactData.assunto}
                                    onClick={() => setContactErrorData((prevData) => ({ ...prevData, email: [] }))}
                                    onChange={handleChange}
                                    className="w-full px-3 py-1.5 2xl:py-2 border border-gray-300 resize-none rounded-md outline-none focus:border-secondary focus-visible:shadow-none"
                                    rows="5"
                                />
                                {contactErrorData.assunto && contactErrorData.assunto[0] && (
                                    <span className="flex justify-between border-b border-red-500 text-xs 2xl:text-sm text-red-500 tracking-tight 2xl:tracking-normal mt-1 pb-1 pl-2">
                                        {contactErrorData.assunto[0]}{' '}
                                        <span
                                            onClick={() => setContactErrorData((prevData) => ({ ...prevData, assunto: [] }))}
                                            className="rotate-45 -my-2 p-2 cursor-pointer"
                                        >
                                            +
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={formLoading}
                        aria-busy={formLoading}
                        className="w-full lg:w-3/4 bg-secondary text-white py-2 px-4 rounded-md hover:bg-opacity-80 disabled:bg-opacity-80 disabled:cursor-not-allowed"
                    >
                        {!formLoading ? 'Salvar' : <Loading />}
                    </button>
                </form>
            </div>
        </>
    );
};

export default FormEditContact;
